/* eslint-disable arrow-body-style */

import React from 'react';
import moment from 'moment';
import { omit } from 'lodash-es';
import { generateUUID, getElementStyleMathValue } from '@gp/utils/common';
import {
  FIXED_HEADER_HEIGHT,
  getProjectDetailsHeaderHeight,
} from '@gp/pages/Pipeline/ProjectDetails/utils';
import {
  FIELD_COMMON_ERR_MSG,
  ACTION_ITEM_STATUS_NAMES_MAPPING,
  ACTION_ITEM_STATUS_NAMES_ENTRIES,
  StaticActionItemCodes,
} from '@gp/constants/const';

import type { IValidation } from '@gp/types/global';
import type { IRiskVO, IGRActionItemDTO, IGRActionItemVO } from '@gp/types/projectUpdate';

const getIsAvailableList = (list?: any): boolean => Array.isArray(list) && list.length > 0;

export const getIsInitialList = (list?: any): boolean => {
  if (!Array.isArray(list) || list.length !== 1) return false;
  return Object.values(omit(list[0], 'id')).every((value) => !value);
};

export const getIsInitialActionItems = (list?: any): boolean => {
  if (!Array.isArray(list) || list.length !== 1) return false;
  return Object.entries(omit(list[0], 'id')).every(([key, value]) =>
    key !== 'status' ? !value : value === StaticActionItemCodes.NotStarted,
  );
};

export const getValidList = (list?: any): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return getIsInitialList(list) ? [] : list;
};

export const getValidActionItems = (list?: any): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return getIsInitialActionItems(list) ? [] : list;
};

export const getIsDuplicateDate = (
  mode: 'add' | 'edit' | '',
  params?: {
    projectUpdates?: any[];
    dateData?: {
      granularity?: 1 | 2 | (number & {});
      date?: string;
    };
    initialDateData?: {
      granularity?: 1 | 2 | (number & {});
      date?: string;
    };
  },
): boolean => {
  const projectUpdates = params?.projectUpdates;
  const dateData = params?.dateData;
  const initialDateData = params?.initialDateData;
  const granularity = dateData?.granularity;
  const date = dateData?.date;
  const initialGranularity = initialDateData?.granularity;
  const initialDate = initialDateData?.date;
  if (mode === '' || !Array.isArray(projectUpdates) || !projectUpdates.length) return false;
  if (mode === 'add') {
    return projectUpdates.find((projectUPdate) => {
      return projectUPdate?.granularity === granularity && projectUPdate?.date === date;
    });
  }
  return projectUpdates.find((projectUPdate) => {
    const isSelf =
      projectUPdate?.granularity === initialGranularity && projectUPdate?.date === initialDate;
    return !isSelf && projectUPdate?.granularity === granularity && projectUPdate?.date === date;
  });
};

export const getIsRisksValid = (risks?: IRiskVO[] | null): boolean => {
  if (!getIsAvailableList(risks)) return true;
  if (getIsInitialList(risks)) return true;
  return risks!.every((risk) => {
    return !!risk?.category && !!risk?.rating;
  });
};

export const getIsWorkplansValid = (workplans?: any[] | null): boolean => {
  if (!getIsAvailableList(workplans)) return true;
  if (getIsInitialActionItems(workplans)) return true;
  return workplans!.every((workplan) => {
    return (
      !!workplan?.due_date &&
      !!workplan?.status &&
      // !!workplan?.user_id &&
      !!workplan?.user_name &&
      !!workplan?.comment?.trim()
    );
  });
};

export const getMaxLength = (selectCategory: any): number => {
  return Number.isNaN(Number.parseInt(selectCategory, 10)) ? 3 : Number(selectCategory);
};

export const getIsListNotEmpty = (list?: any[] | null): boolean => {
  if (!Array.isArray(list) || !list.length) return false;
  return list.some((item) => {
    return Object.values(omit(item || {}, 'id')).some((subItem) => !!subItem);
  });
};

const projectUpdateFormValidateFields = [
  { key: 'dateData', checkKey: 'isDateDataValid' },
  { key: 'keyUpdates', checkKey: 'isKeyUpdatesValid' },
  { key: 'risks', checkKey: 'isRisksValid' },
  { key: 'workplans', checkKey: 'isWorkplansValid' },
];
export const getProjectUpdateFormValidations = (params: {
  isDateDataValid: boolean;
  isKeyUpdatesValid: boolean;
  isRisksValid: boolean;
  isWorkplansValid: boolean;
  [key: string]: boolean;
}): IValidation[] => {
  const validations: IValidation[] = [];
  for (const projectUpdateFormValidateField of projectUpdateFormValidateFields) {
    const { key, checkKey } = projectUpdateFormValidateField;
    if (!params?.[checkKey]) {
      validations.push({
        key,
        message: FIELD_COMMON_ERR_MSG,
      });
    }
  }
  return validations;
};

export const getProjectUpdateFormFieldValidation = (
  key?: string,
  submitted?: boolean,
  editValid?: boolean,
  validations?: IValidation[],
) => {
  if (!submitted || editValid) return null;
  return validations?.find((validation) => validation?.key === key) || null;
};

const getStdDatesValue = (dates?: string | [string, string]): [string, string] => {
  if (!dates) return ['', ''];
  if (!Array.isArray(dates)) return !!dates ? [dates, dates] : ['', ''];
  const [startDate, endDate] = dates;
  return !startDate || !endDate ? ['', ''] : [startDate, endDate];
};

export const getUpdatesDateValue = (
  type: 'month' | 'week' | (string & {}),
  dates: string | [string, string],
): [string, string] => {
  const [startDate, endDate] = getStdDatesValue(dates);
  if (!startDate || !endDate) return ['', ''];
  if (type === 'week') return [startDate, endDate];
  if (type === 'month') return [startDate, moment(startDate).endOf('month').format('YYYY-MM-DD')];
  return [startDate, endDate];
};

export const formatGRActionItemsRes = (list?: IGRActionItemDTO[] | null): IGRActionItemVO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const {
      id,
      user,
      action_content: actionContent,
      state,
      state_comment: stateComment,
      ...restItem
    } = item || {};
    const [status] = ACTION_ITEM_STATUS_NAMES_ENTRIES.find(([_code, name]) => name === state) || [];
    return {
      ...restItem,
      id: generateUUID(id),
      comment: actionContent || '',
      status: status || '',
      status_comment: stateComment || '',
      user_id: user || '',
      user_name: user || '',
    };
  });
};

export const formatGRActionItemsReq = (list?: IGRActionItemVO[] | null): IGRActionItemDTO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const {
      comment,
      status,
      status_comment: statusComment,
      user_id: userId,
      user_name: userName,
      ...restItem
    } = item || {};
    const user = userName || userId || '';
    // const user = userId || userName || '';
    return {
      ...restItem,
      action_content: comment || '',
      state: ACTION_ITEM_STATUS_NAMES_MAPPING[status] || '',
      state_comment: statusComment || '',
      user,
    };
  });
};

const getOthersDistance = (detailsPageNode?: Element | null): number => {
  const detailsPageTeaBodyNode = detailsPageNode?.querySelector('.tea-layout__content-body');
  const updatesTabPageToolbarNode = detailsPageNode?.querySelector(
    '.dbg-detail-project-update-flex',
  );

  // project详情页头部总高度
  const headerHeight = getProjectDetailsHeaderHeight(detailsPageNode);
  const detailsPageTeaBodyNodePaddingTop = getElementStyleMathValue(
    detailsPageTeaBodyNode as any,
    'padding-top',
  );
  const updatesTabPageToolbarNodeHeight = getElementStyleMathValue(
    updatesTabPageToolbarNode as any,
    'height',
  );
  const updatesTabPageToolbarNodeMarginTop = getElementStyleMathValue(
    updatesTabPageToolbarNode as any,
    'margin-top',
  );
  const updatesTabPageToolbarNodeMarginBottom = getElementStyleMathValue(
    updatesTabPageToolbarNode as any,
    'margin-bottom',
  );
  return (
    headerHeight +
    detailsPageTeaBodyNodePaddingTop +
    updatesTabPageToolbarNodeHeight +
    updatesTabPageToolbarNodeMarginTop +
    updatesTabPageToolbarNodeMarginBottom
  );
};

export const scrollUpdatesCardIntoView = (
  pageContainer?: React.RefObject<HTMLElement | null>,
  index?: number,
) => {
  if (!pageContainer?.current) return;
  if (index === undefined || index === -1) return;
  if (index === 0) {
    pageContainer?.current?.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  // project详情页顶层节点
  const detailsPageNode = document.querySelector('.dbg-project-details');
  // updates卡片节点集合
  const updatesNodes = detailsPageNode?.querySelector(
    '.dbg-detail-project-update-content',
  )?.children;
  if (!updatesNodes) return;

  let updatesDistance = 0;

  for (let i = 0; i < updatesNodes.length; i++) {
    if (i === index) break;
    const updatesNode = updatesNodes[i];
    const updatesNodeHeight = getElementStyleMathValue(updatesNode as any, 'height');
    const updatesNodeMarginTop = getElementStyleMathValue(updatesNode as any, 'margin-top');
    const updatesNodeMarginBottom = getElementStyleMathValue(updatesNode as any, 'margin-bottom');
    updatesDistance =
      updatesDistance + (updatesNodeHeight + updatesNodeMarginTop + updatesNodeMarginBottom);
  }

  const scrollDistance = updatesDistance + getOthersDistance(detailsPageNode) - FIXED_HEADER_HEIGHT;

  pageContainer?.current?.scrollTo({ top: scrollDistance, behavior: 'smooth' });
};

export const getGRActionItemsFormItemLabel = (milestone?: string): React.ReactElement => {
  const showMilestone = !!milestone;
  return (
    <>
      Action Items from Previous GR
      {showMilestone && (
        <>
          <br />({milestone})
        </>
      )}
    </>
  );
};
